/**
 * @module components/modals/ContextMenu
 * @summary Right-click / long-press canvas context menu.
 * @description Registers itself as the engine's context-menu handler; the
 *   engine selects the shape under the cursor and asks this component to open
 *   at the given client position. Menu actions call the engine API.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 565-581 + 4107-4140.
 */
import { useEffect, useState } from 'react';
import { engine } from '../../engine/CanvasEngine';

interface MenuState {
  x: number;
  y: number;
  hasSelection: boolean;
  hasClipboard: boolean;
}

export function ContextMenu() {
  const [menu, setMenu] = useState<MenuState | null>(null);

  useEffect(() => {
    engine.contextMenuHandler = (info) =>
      setMenu({
        x: info.clientX,
        y: info.clientY,
        hasSelection: info.hasSelection,
        hasClipboard: info.hasClipboard,
      });
    const close = () => setMenu(null);
    document.addEventListener('click', close);
    return () => {
      engine.contextMenuHandler = null;
      document.removeEventListener('click', close);
    };
  }, []);

  if (!menu) return null;

  const run = (fn: () => void) => () => {
    fn();
    setMenu(null);
  };
  const Item = ({
    label,
    sc,
    onClick,
    disabled,
    danger,
  }: {
    label: string;
    sc?: string;
    onClick: () => void;
    disabled?: boolean;
    danger?: boolean;
  }) => (
    <div
      className={'ctx-item' + (disabled ? ' disabled' : '') + (danger ? ' danger' : '')}
      onClick={disabled ? undefined : run(onClick)}
    >
      <span>{label}</span>
      {sc && <span className="sc">{sc}</span>}
    </div>
  );

  return (
    <div id="ctx-menu" style={{ display: 'block', left: menu.x, top: menu.y }}>
      <Item label="Copy" sc="Ctrl+C" disabled={!menu.hasSelection} onClick={() => engine.copy()} />
      <Item label="Paste" sc="Ctrl+V" disabled={!menu.hasClipboard} onClick={() => engine.paste()} />
      <Item label="Duplicate" sc="Ctrl+D" disabled={!menu.hasSelection} onClick={() => engine.duplicate()} />
      <div className="ctx-sep" />
      <Item label="Bring to Front" disabled={!menu.hasSelection} onClick={() => engine.bringToFront()} />
      <Item label="Send to Back" disabled={!menu.hasSelection} onClick={() => engine.sendToBack()} />
      <div className="ctx-sep" />
      <Item label="Rotate 90° CW" disabled={!menu.hasSelection} onClick={() => engine.rotate(90)} />
      <Item label="Rotate 90° CCW" disabled={!menu.hasSelection} onClick={() => engine.rotate(-90)} />
      <div className="ctx-sep" />
      <Item label="Lock / Unlock" disabled={!menu.hasSelection} onClick={() => engine.toggleLock()} />
      <Item label="↺ Reset connection routing" disabled={!menu.hasSelection} onClick={() => engine.resetRoute()} />
      <div className="ctx-sep" />
      <Item label="Delete" sc="Del" danger disabled={!menu.hasSelection} onClick={() => engine.delete()} />
    </div>
  );
}
